import { useEffect, type RefObject } from "react";
import {
  isNativeKeyboardFocusSupported,
  requestNativeKeyboardFocus,
} from "@/shared/lib/keyboardFocusBridge";

type UseAutoFocusOnEnterOptions = {
  /** false 면 자동 포커스를 무장하지 않는다(no-op). 기본 true. */
  enabled?: boolean;
};

// 진입 전환이 끝나 요소가 정착하지 않아도 강제로 focus 하는 상한(ms).
// --page-transition-duration(340ms) + 여유. reduced-motion=0(전환 미등록) 이나
// getAnimations 미지원 환경에서 정착 신호가 안 잡힐 때의 안전망이다.
const SETTLE_TIMEOUT_MS = 400;

// 진입 시 부여되는 -active 클래스가 적용돼 CSS transition 이 애니메이션 레지스트리에
// 등록될 시간을 벌기 위해 양보하는 프레임 수. 이 프레임을 건너뛰면 "아직 전환이
// 시작되지 않아 running 이 0개" 인 순간을 정착으로 오판해 조기 focus 한다.
const WARMUP_FRAMES = 2;

/** 편집 요소가 실제로 보이고 활성 focus 가 유효한 상태인지. */
const isEditableVisible = (el: HTMLElement): boolean =>
  el.isConnected &&
  // display:none(및 그 조상)일 때 null. 접힌 CollapsibleField 는 아래 aria-hidden 으로 잡는다.
  el.offsetParent !== null &&
  el.closest('[aria-hidden="true"]') === null;

// iOS 진입 자동 키보드: native(evaluateJavaScript)가 부르는 전역 focus 함수(window.__focusEntryField)
// 의 대상 요소. useKeyboardOpen 의 baselineHeight 처럼 모듈 스코프여야 콜사이트가 많아도(14) 한 곳에
// 모인다 — 화면당 실질 1개만 enabled(=정착)라 "마지막 정착 wins" 로 안전하다. 인스턴스 teardown 이
// 자기 el 일 때만 해제한다(아래 effect cleanup).
let currentEntryTarget: HTMLElement | null = null;
let focusEntryFieldRegistered = false;

// window.__focusEntryField 를 1회 등록한다. native 가 requestKeyboardFocus 수신 시 동기 호출한다
// (키보드가 올라오는 근거는 keyboardFocusBridge.ts 헤더). 매 호출 isEditableVisible 로 재검증하므로
// 대상이 사라졌으면 false(무해).
const ensureFocusEntryFieldRegistered = (): void => {
  if (focusEntryFieldRegistered) return;
  focusEntryFieldRegistered = true;
  window.__focusEntryField = (): boolean => {
    const el = currentEntryTarget;
    if (el && isEditableVisible(el)) {
      el.focus();
      return true;
    }
    return false;
  };
};

/**
 * 대상 input 또는 그 조상에서 아직 running 인 CSS transition/animation 이 하나라도 있는지.
 *
 * 진입 애니메이션은 input 자신이 아니라 상위 프레임(route-wrapper=transform/opacity,
 * step frame=transform(카드 슬라이드), CollapsibleField .field=max-height)에서 일어난다. `transitionend` 는
 * 애니메이션 요소에서 위로만 버블링해 input 리스너로는 못 받으므로, 애니메이션 레지스트리를
 * 직접 조회해 "target 이 input 자신이거나 input 을 포함(조상)하는 running 애니메이션" 유무를 본다.
 * property·노드 비의존이라 slide/fade/collapse 세 컨텍스트를 한 번에 커버한다.
 *
 * getAnimations 미지원(하한 Safari 14.1 은 지원, 13.1+) 이면 false 를 반환해 타임아웃 폴백에 위임한다.
 */
const hasRunningEnterAnimation = (el: HTMLElement): boolean => {
  if (typeof document.getAnimations !== "function") return false;
  return document.getAnimations().some((anim) => {
    if (anim.playState !== "running") return false;
    const target = (anim.effect as KeyframeEffect | null)?.target ?? null;
    return target instanceof Element && (target === el || target.contains(el));
  });
};

/**
 * 진입 시 대상 input 자동 포커스 — 고정 delay 가 아니라 진입 전환이 끝나 정착한 시점에(ADR 260806-01). 전환 중 focus() 하면 Android 가 showSoftInput 을 조용히 no-op 해 "활성화는 되는데 키보드가 안 뜨는" 간헐 버그가 난다. focus 트리거만 담당(어떤 ref 를 언제는 호출처가 enabled/ref 로, 스크롤은 useScrollFocusedFieldIntoView). iOS 는 제스처 밖 focus 로 키보드를 안 올리므로 신네이티브면 keyboardFocusBridge 경유(finish 의 분기 주석 참조).
 *
 * @param ref     포커스할 실제 input 요소 ref (Input 이 useImperativeHandle 로 노출하는 것)
 * @param options enabled=false 면 무장하지 않는다(기본 true)
 */
export function useAutoFocusOnEnter(
  ref: RefObject<HTMLElement | null>,
  options?: UseAutoFocusOnEnterOptions,
): void {
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    let rafId: number | null = null;
    let timerId: number | null = null;
    let done = false;
    let warmup = WARMUP_FRAMES;

    const cleanup = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (timerId !== null) window.clearTimeout(timerId);
      rafId = null;
      timerId = null;
    };

    const finish = () => {
      if (done) return;
      done = true;
      cleanup();
      // 정착 후에도 여전히 보이는 편집 요소일 때만 진행 (전환 중 언마운트/접힘 회피).
      if (!isEditableVisible(el)) return;
      // iOS 신네이티브면 el.focus() 를 직접 부르지 않는다 — 그 focus 는 제스처 밖이라 키보드를 안
      // 올리고, 뒤이어 native 가 부르는 __focusEntryField() 의 focus 는 "이미 focused" 라 no-op 이 된다.
      // 대신 대상만 세팅하고 native 에 요청 → native 가 eval 스코프에서 첫 focus 로 키보드를 올린다.
      if (isNativeKeyboardFocusSupported()) {
        currentEntryTarget = el;
        ensureFocusEntryFieldRegistered();
        requestNativeKeyboardFocus();
      } else {
        // Android(정착 후 focus 로 키보드 정상) / iOS 구네이티브(테두리) / 데스크톱.
        el.focus();
      }
    };

    // 안전망: 정착 신호가 안 잡히거나 getAnimations 미지원이면 상한에서 강제 진행.
    timerId = window.setTimeout(finish, SETTLE_TIMEOUT_MS);

    const poll = () => {
      if (done) return;
      if (warmup > 0) {
        warmup -= 1;
        rafId = requestAnimationFrame(poll);
        return;
      }
      if (hasRunningEnterAnimation(el)) {
        rafId = requestAnimationFrame(poll);
        return;
      }
      finish();
    };
    rafId = requestAnimationFrame(poll);

    return () => {
      cleanup();
      // native 경로에서 이 인스턴스가 세팅한 대상이면 해제(위생). el 은 effect 캡처값 —
      // ref.current 는 unmount 시 이미 null 일 수 있어 캡처값 el 로 비교한다.
      if (currentEntryTarget === el) currentEntryTarget = null;
    };
  }, [enabled, ref]);
}
